import { soundManifest, type SoundKey } from "./sounds";

type LoadedSound = {
  buffer: AudioBuffer;
  volume: number;
};

export class AudioManager {
  private context: AudioContext | null = null;
  private sounds = new Map<SoundKey, LoadedSound>();
  private masterVolume = 0.8;
  private enabled = true;
  private loading: Promise<void> | null = null;
  private preloadRequested = false;

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  public setMasterVolume(volume: number): void {
    this.masterVolume = clamp(volume, 0, 1);
  }

  public async preload(): Promise<void> {
    this.preloadRequested = true;

    if (!this.context) {
      return;
    }

    return this.loadAllOnce(this.context);
  }

  public async unlock(): Promise<void> {
    const context = this.getContext();

    if (context.state === "suspended") {
      await context.resume();
    }

    if (this.preloadRequested || this.sounds.size === 0) {
      await this.loadAllOnce(context);
    }
  }

  public play(key: SoundKey, volumeMultiplier = 1): void {
    if (!this.enabled || !this.context || this.context.state !== "running") {
      return;
    }

    const sound = this.sounds.get(key);

    if (!sound) {
      return;
    }

    const source = this.context.createBufferSource();
    const gain = this.context.createGain();

    source.buffer = sound.buffer;
    gain.gain.value = sound.volume * this.masterVolume * clamp(volumeMultiplier, 0, 2);

    source.connect(gain);
    gain.connect(this.context.destination);
    source.start();
  }

  private async loadAllOnce(context: AudioContext): Promise<void> {
    if (this.loading) {
      return this.loading;
    }

    this.loading = this.loadAll(context);
    return this.loading;
  }

  private async loadAll(context: AudioContext): Promise<void> {
    await Promise.all(
      soundManifest.map(async (definition) => {
        const buffer = await this.tryLoad(context, definition.path);

        if (buffer) {
          this.sounds.set(definition.key, {
            buffer,
            volume: definition.volume
          });
        }
      })
    );
  }

  private async tryLoad(context: AudioContext, path: string): Promise<AudioBuffer | null> {
    try {
      const response = await fetch(path, { cache: "force-cache" });

      if (!response.ok) {
        return null;
      }

      const data = await response.arrayBuffer();
      return await context.decodeAudioData(data);
    } catch {
      return null;
    }
  }

  private getContext(): AudioContext {
    if (!this.context) {
      this.context = new AudioContext();
    }

    return this.context;
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}